import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import StepForm from './StepForm'
import { tiers } from '../data/content'
import { growthPlans } from '../data/growthPlans'
import { buildWhatsAppLink } from '../data/config'
import Button from './Button'
import { trackContact } from '../lib/tiktokPixel'
import { trackContact as trackContactLinkedIn } from '../lib/linkedInInsight'

const TIER_LABEL = Object.fromEntries(tiers.map((t) => [t.id, t.name]))
const GROWTH_PLAN_LABEL = Object.fromEntries(growthPlans.map((p) => [p.id, p.name]))

function encodeForNetlify(data) {
  return Object.keys(data)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
    .join('&')
}

/**
 * Rebuilt as a SideShift-style multi-step flow (see StepForm.jsx),
 * replacing the previous single long form. The first step is now plan
 * selection itself — the small pill-tabs that used to sit above the form
 * are gone, absorbed into step 1 — and the questions that follow adapt to
 * which plan was picked, same branching logic the old form had
 * (isScale / isAudit / isStrategy), just expressed as step lists instead
 * of inline JSX branches.
 *
 * A pre-selected tier (via onSelectTier from a pricing card elsewhere on
 * the page) or an audit/strategy landing context still pre-fills step 1
 * the same way the old form pre-filled the pill-tabs.
 *
 * Submission mechanics unchanged: form-name "inquiry", fetch + static
 * hidden Netlify form, redirect to /thank-you on success.
 */
export default function InquiryForm({
  selectedTier,
  onSelectTier,
  promise = 'strategy',
  selectedGrowthPlan = null,
  // When rendered inside FormModal.jsx instead of inline on the page: skips
  // the outer <section id="inquiry"> wrapper/padding (the modal shell
  // already provides its own chrome and padding), and calls onSuccess
  // (closes the modal) right before the /thank-you redirect fires.
  inModal = false,
  onSuccess,
  // Modal-only escape hatch: closes the popup (without submitting)
  // and scrolls the page to the static #pricing table — for visitors who
  // opened "Start My Website" but actually just wanted to compare plans
  // and prices, not commit to one yet. See the plan step's helperLink
  // below.
  onRequestClose,
}) {
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(false)
  const navigate = useNavigate()

  const isScale = selectedTier === 'scale'
  const isAudit = !isScale && (promise === 'audit' || selectedTier === 'growth')
  const isStrategy = !isScale && !isAudit && promise === 'strategy' && !selectedTier

  useEffect(() => {
    setError(false)
  }, [selectedTier])

  const steps = useMemo(() => {
    const planStep = {
      id: 'tier',
      title: 'Which plan fits you?',
      subtitle: 'Pick one to start — you can change your mind anytime before sending. Not sure? Each option below shows who it fits and the starting price.',
      type: 'choice',
      required: true,
      options: tiers.map((t) => ({
        label: t.name,
        description: `${t.for} ${t.price}${t.usdPrice ? ` (${t.usdPrice})` : ''}.`,
      })),
      // Modal-only: someone who just wants to compare plans side by side
      // rather than pick one blind shouldn't be stuck in the flow —
      // bails out to the real pricing table instead.
      ...(inModal
        ? {
            helperLink: {
              label: 'Not ready to pick? See full pricing details instead',
              onClick: () => {
                onRequestClose?.()
                requestAnimationFrame(() => {
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
                })
              },
            },
          }
        : {}),
    }

    const contactSteps = [
      { id: 'name', title: "What's your name?", type: 'text', placeholder: 'Your name', required: true },
      { id: 'business', title: "What's the business called?", type: 'text', placeholder: 'Business name (optional)' },
      { id: 'email', title: 'Where can we reach you?', type: 'email', placeholder: 'name@business.com', required: true },
      { id: 'phone', title: 'WhatsApp or phone?', subtitle: 'Optional, if email is easier.', type: 'text', placeholder: '+234…' },
    ]

    const tierId = selectedTier

    let tailSteps = []
    if (tierId === 'scale') {
      tailSteps = [
        { id: 'goals', title: 'What are you trying to achieve?', type: 'textarea', required: true },
        { id: 'timeline', title: 'Ideal timeline?', type: 'text', placeholder: 'e.g. 6–8 weeks' },
        { id: 'integrations', title: 'Any integrations needed?', type: 'text', placeholder: 'e.g. payments, booking' },
        { id: 'requirements', title: 'Any custom requirements?', type: 'textarea' },
      ]
    } else if (tierId === 'growth' || isAudit) {
      tailSteps = [
        { id: 'currentSite', title: 'Link to your current website', subtitle: 'A live site, or an Instagram page.', type: 'text', placeholder: 'yourbusiness.com or an Instagram page' },
        { id: 'details', title: "What's not working about it right now?", type: 'textarea', placeholder: "e.g. it looks outdated, it doesn't convert, it's hard to update…" },
      ]
    } else if (tierId === 'launch') {
      tailSteps = [
        { id: 'currentSite', title: 'Link to your current website (if you have one)', type: 'text', placeholder: 'yourbusiness.com or an Instagram page' },
        { id: 'details', title: 'Tell us about your business', type: 'textarea' },
      ]
    } else {
      tailSteps = [
        { id: 'currentSite', title: 'Link to your current website (if you have one)', type: 'text', placeholder: 'yourbusiness.com or an Instagram page' },
        { id: 'details', title: 'What made you want to talk?', type: 'textarea', placeholder: "e.g. leads aren't converting, the site feels dated, comparing yourself to a competitor…" },
      ]
    }

    return [planStep, ...contactSteps, ...tailSteps]
  }, [selectedTier, isAudit, inModal, onRequestClose])

  const handleComplete = async (values) => {
    if (sending) return
    setSending(true)
    setError(false)

    const pickedTierId = tiers.find((t) => t.name === values.tier)?.id || selectedTier || 'unspecified'
    if (onSelectTier && pickedTierId !== selectedTier) {
      onSelectTier(pickedTierId)
    }

    const payload = {
      'form-name': 'inquiry',
      // Explicit empty honeypot — Netlify's spam filter checks this field
      // (declared on the hidden static form in index.html) and rejects
      // any submission where it's filled in. Real visitors never see it;
      // basic bots that auto-fill every field they find do.
      company: '',
      growthPlan: selectedGrowthPlan || '',
      ...values,
      tier: pickedTierId,
    }

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encodeForNetlify(payload),
      })
      trackContact({ tier: pickedTierId, growthPlan: selectedGrowthPlan })
      trackContactLinkedIn()
      onSuccess?.()
      navigate('/thank-you')
    } catch (err) {
      setError(true)
    } finally {
      setSending(false)
    }
  }

  const content = (
    <>
        <div className="mb-10">
          <p className="eyebrow mb-4">
            {selectedTier
              ? `${TIER_LABEL[selectedTier]} Plan`
              : isAudit
              ? 'Free Website Audit'
              : isStrategy
              ? 'Not Ready to Book Yet?'
              : 'Get Started'}
          </p>
          <h2 className="font-display text-3xl md:text-4xl" style={{ color: 'var(--text-primary)' }}>
            {isScale
              ? 'Tell us about your project'
              : isAudit
              ? "Let's look at your current site"
              : isStrategy
              ? 'Send us a message instead'
              : "Let's start the conversation"}
          </h2>
          {isAudit && (
            <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
              Share a link to what you have now. We'll walk through it together and tell you
              what's working, what isn't, and what a redesign should fix.
            </p>
          )}
          {isStrategy && (
            <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
              Prefer not to book a call right away? Pick a plan below and tell us a bit about your
              business, and we'll get back to you.
            </p>
          )}
          {selectedGrowthPlan && (
            <p
              className="mt-4 inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full"
              style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
            >
              Also interested in: {GROWTH_PLAN_LABEL[selectedGrowthPlan]}
            </p>
          )}
        </div>

        <StepForm
          key={selectedTier || 'default'}
          steps={steps}
          onComplete={handleComplete}
          sending={sending}
          submitLabel={isScale ? 'Send Project Brief' : 'Send Message'}
        />

        {error && (
          <p className="text-sm mt-4" style={{ color: 'var(--text-secondary)' }}>
            Something went wrong sending that. Try again, or use WhatsApp below.
          </p>
        )}

        <div className="mt-6">
          <Button href={buildWhatsAppLink(selectedTier && TIER_LABEL[selectedTier])} variant="secondary" className="!rounded-full">
            Chat on WhatsApp instead
          </Button>
        </div>
    </>
  )

  if (inModal) return content

  return (
    <section id="inquiry" className="py-24 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="container-bw max-w-2xl">{content}</div>
    </section>
  )
}
